/**
 * responseSimplicityBalancer — Determines when Kevia's response should be structurally simpler.
 *
 * Simplicity triggers (high priority):
 *   grief, critical exhaustion, emotional silence, bereavement
 * Simplicity triggers (medium priority):
 *   overwhelmed capacity, depleted state, closed/defensive readiness
 *
 * Returns { simplicityLevel, triggers, instruction }
 */

const SIMPLICITY_RULES = [
  {
    trigger:     "grief",
    test:        ({ emotion }) =>
      emotion?.dominant === "duelo" || emotion?.dominant === "dolor_profundo" || emotion?.dominant === "pérdida",
    level:       "high",
    instruction: "Un párrafo. Sin estructura. Sin pasos de acción.",
  },
  {
    trigger:     "exhaustion_critical",
    test:        ({ exhaustionState }) =>
      exhaustionState === "saturación_crítica" || exhaustionState === "desgaste_profundo",
    level:       "high",
    instruction: "Sin agenda pastoral. Solo presencia y alivio.",
  },
  {
    trigger:     "emotional_silence",
    test:        ({ emotion, silentTension }) =>
      emotion?.dominant === "silencio_emocional" || (silentTension ?? 0) >= 3,
    level:       "high",
    instruction: "Muy pocas palabras. El silencio tiene más peso que el análisis.",
  },
  {
    trigger:     "overwhelmed",
    test:        ({ capacity }) => capacity === "overwhelmed",
    level:       "medium",
    instruction: "Sin listas. Sin marcos. Solo lo esencial.",
  },
  {
    trigger:     "depleted",
    test:        ({ capacity, exhaustionState }) =>
      capacity === "depleted" || exhaustionState === "agotamiento_progresivo",
    level:       "medium",
    instruction: "Menos es más. Una sola idea central.",
  },
  {
    trigger:     "closed_defensive",
    test:        ({ readiness }) => readiness === "cerrado" || readiness === "defensivo",
    level:       "medium",
    instruction: "No profundizar. Solo escuchar y validar.",
  },
];

export function balanceResponseSimplicity({ emotion, exhaustionState, silentTension, capacity, readiness }) {
  const triggers = [];

  for (const rule of SIMPLICITY_RULES) {
    if (rule.test({ emotion, exhaustionState, silentTension, capacity, readiness })) {
      triggers.push({ trigger: rule.trigger, level: rule.level, instruction: rule.instruction });
    }
  }

  const highTriggers   = triggers.filter((t) => t.level === "high");
  const mediumTriggers = triggers.filter((t) => t.level === "medium");

  let simplicityLevel;
  let instruction = null;

  if (highTriggers.length > 0) {
    simplicityLevel = "high";
    instruction     = highTriggers[0].instruction;
  } else if (mediumTriggers.length > 0) {
    simplicityLevel = "medium";
    instruction     = mediumTriggers[0].instruction;
  } else {
    simplicityLevel = "normal";
  }

  return { simplicityLevel, triggers, instruction };
}
